import {Map, fromJS} from 'immutable';
import type {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {ActionType} from '@packages/utilities/create-action';
import {
  PRODUCTS_LIST_CHANGED,
  DYN_ITEM_SELECTED,
  DYN_ITEM_MINTED,
  DYN_ITEM_DESELECTED,
  COMMERCE_ITEM_SAVED,
  RAW_ITEMS_RECEIVED,
} from '@packages/systems/designer/actions';
import {
  isCategoriesCollection,
  getCategoriesProductsMap,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  type ProductPickerStateType,
  ProductPickerState,
} from '@packages/systems/commerce/core';
import {
  mintDynItem,
  selectDynItem,
  deselectDynItem,
  receiveRawItems,
} from '@packages/systems/cms/CollectionStore';
import {saveCommerceItem, changeProductList} from './actions';
import {CurrentCollectionStateType} from '@packages/systems/cms/types';

type ProductPickerStoreAction = ActionType<
  | typeof mintDynItem
  | typeof receiveRawItems
  | typeof changeProductList
  | typeof saveCommerceItem
  | typeof selectDynItem
  | typeof deselectDynItem
>;

type OtherStores = {
  collectionStore: CollectionStateType;
  currentCollectionStore: CurrentCollectionStateType;
};

export const initialState: ProductPickerStateType = ProductPickerState();

export default function ProductPickerStore(
  state: ProductPickerStateType,
  action: ProductPickerStoreAction,
  dependencies: OtherStores
) {
  switch (action.type) {
    case DYN_ITEM_MINTED: {
      return initialState;
    }

    case DYN_ITEM_SELECTED: {
      const {
        collectionStore: {items, collections},
        currentCollectionStore: {currentCollection},
      } = dependencies;
      const {id} = action.payload;

      if (!isCategoriesCollection(currentCollection)) {
        return state;
      }

      const categoriesMap = getCategoriesProductsMap(items, collections);
      const value = fromJS(categoriesMap[id]);

      return state.set('categoryId', id).set('products', value);
    }

    case RAW_ITEMS_RECEIVED: {
      const {
        collectionStore: {collections},
        currentCollectionStore: {currentCollection, currentItem},
      } = dependencies;

      if (!isCategoriesCollection(currentCollection)) {
        return state;
      }

      if (!currentItem) {
        return state;
      }

      const itemsById = action.payload.items.reduce((acc, item) => {
        return acc.set(item.get('_id'), item);
      }, Map());

      const categoriesMap = getCategoriesProductsMap(itemsById, collections);
      const products = fromJS(categoriesMap[currentItem.get('_id')]);

      if (!products) {
        return state;
      }

      return state.update('products', (currentProducts) => {
        if (!currentProducts) {
          return products;
        }

        return currentProducts.concat(
          // @ts-expect-error - flow thinks this type is mixed but it should be List
          products.filter((product) => {
            // We only want to add unique values to this
            return !currentProducts.includes(product);
          })
        );
      });
    }

    case PRODUCTS_LIST_CHANGED: {
      const {
        payload: {value},
      } = action;
      return state.set('products', value);
    }

    case DYN_ITEM_DESELECTED: {
      const {item} = action.payload;
      if (item) {
        // reset products in mint category form after mint inline category is canceled
        return isCategoriesCollection(item.get('collection'))
          ? initialState
          : state;
      }
      return state;
    }

    case COMMERCE_ITEM_SAVED: {
      const wasMintInlineItem =
        dependencies.currentCollectionStore.currentInlineItem;
      // resets products in mint category form after mint inline category is saved
      return wasMintInlineItem ? initialState : state;
    }
    default: {
      return state;
    }
  }
}
